import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Edit, Delete } from '@mui/icons-material';
import accountingService from '../../services/accounting.service';
import { useLanguage } from '../../i18n/LanguageContext';

const AccountList: React.FC = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => { loadAccounts(); }, []);

  const loadAccounts = async () => {
    try {
      const response = await accountingService.getAccounts(true);
      setAccounts(response.data);
    } catch (error) { console.error('Error loading accounts:', error); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('accounts.delete_confirm'))) return;
    try {
      await accountingService.deleteAccount(id);
      loadAccounts();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error deleting account');
    }
  };

  const getTypeColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors: Record<string, any> = {
      'ASSET': 'primary', 'LIABILITY': 'error', 'EQUITY': 'secondary',
      'REVENUE': 'success', 'EXPENSE': 'warning'
    };
    return colors[type] || 'default';
  };

  const columns: GridColDef[] = [
    { field: 'accountCode', headerName: t('accounts.account_code'), width: 100 },
    { field: 'accountName', headerName: t('accounts.account_name'), flex: 1 },
    {
      field: 'accountType', headerName: t('accounts.account_type'), width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} color={getTypeColor(params.value as string)} size="small" />
      )
    },
    { field: 'parentAccountName', headerName: t('accounts.parent_account'), width: 180 },
    {
      field: 'balance', headerName: t('accounts.balance'), width: 130, align: 'right', headerAlign: 'right',
      renderCell: (params: GridRenderCellParams) => `$${Number(params.value).toFixed(2)}`
    },
    {
      field: 'isActive', headerName: t('common.status'), width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value ? t('common.active') : t('common.inactive')} color={params.value ? 'success' : 'default'} size="small" />
      )
    },
    {
      field: 'actions', headerName: t('common.actions'), width: 120, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title={t('common.edit')}>
            <IconButton size="small" onClick={() => navigate(`/accounts/edit/${params.row.id}`)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('accounts.title')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/accounts/new')}>
          {t('accounts.new')}
        </Button>
      </Box>
      <DataGrid rows={accounts} columns={columns} loading={loading} autoHeight
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25, 50, 100]} disableRowSelectionOnClick />
    </Box>
  );
};

export default AccountList;
